import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { HttpErrorResponse } from '@angular/common/http';
import dayjs, { Dayjs } from 'dayjs';
import { DashboardComponentInterface } from '../../../../models/dashboard';
import {
  DailyScanTimesDashboardService,
  DayScanTimes,
} from './daily-scan-times-dashboard.service';
import { DailyScanTimesDashboardConfiguration } from './daily-scan-times-dashboard-configuration';
import { JobDashboardRange } from '../job-dashboard/job-dashboard-configuration';

@Component({
  selector: 'app-daily-scan-times-dashboard',
  templateUrl: './daily-scan-times-dashboard.component.html',
  styleUrls: ['./daily-scan-times-dashboard.component.scss'],
  imports: [CommonModule, RouterLink, LoadingIndicatorComponent, TranslatePipe],
})
export class DailyScanTimesDashboardComponent
  implements OnInit, OnDestroy, DashboardComponentInterface
{
  private authorizationService = inject(AuthorizationService);
  private service = inject(DailyScanTimesDashboardService);
  private translate = inject(TranslateService);
  @Input() configuration!: DailyScanTimesDashboardConfiguration;

  intervalTime = 600000; // 10 minutes for analytics
  protected days: DayScanTimes[] = [];
  protected warnings: string[] = [];
  protected error: string | null = null;
  protected isLoading = false;
  protected dateFrom?: Dayjs;
  protected dateTo?: Dayjs;
  private interval?: ReturnType<typeof setInterval>;
  private isFirstLoad = true;

  protected canNavigateToJobs =
    this.authorizationService.hasPermission('job.read');

  private readonly defaultConfig: Partial<DailyScanTimesDashboardConfiguration> =
    {
      range: JobDashboardRange.TODAY,
      sortOrder: 'desc',
    };

  ngOnInit() {
    this.interval = setInterval(() => this.loadData(), this.intervalTime);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

  setConfiguration(configuration: unknown): void {
    this.configuration = {
      ...this.defaultConfig,
      ...this.configuration,
      ...(configuration as object),
    };
  }

  loadData() {
    const scanTimestampFieldKey = this.configuration.scanTimestampFieldKey;
    if (!scanTimestampFieldKey) {
      this.resetState();
      this.isLoading = false;
      return;
    }

    // Compute the date range used
    if (this.configuration.range === JobDashboardRange.CUSTOM) {
      this.dateFrom = this.configuration.from
        ? dayjs(this.configuration.from)
        : undefined;
      this.dateTo = this.configuration.to
        ? dayjs(this.configuration.to)
        : undefined;
    } else {
      const now = dayjs();
      this.dateFrom = this.service.getFromForRange(
        this.configuration.range,
        now,
      );
      this.dateTo = this.service.getToForRange(this.configuration.range, now);
    }

    // A custom range without both dates is treated like an unconfigured widget
    if (!this.dateFrom || !this.dateTo) {
      this.resetState();
      this.isLoading = false;
      return;
    }

    if (this.isFirstLoad) {
      this.isFirstLoad = false;
      this.isLoading = true;
    }
    this.service
      .getData(
        this.configuration,
        this.dateFrom,
        this.dateTo,
        scanTimestampFieldKey,
      )
      .subscribe({
        next: (response) => {
          const direction = this.configuration.sortOrder === 'asc' ? 1 : -1;
          this.days = [...response.days].sort(
            (a, b) => direction * a.date.localeCompare(b.date),
          );
          this.warnings = response.warnings;
          this.error = null;
          this.isLoading = false;
        },
        error: (err: HttpErrorResponse) => {
          const errorMessage = this.getErrorMessage(err);
          this.resetState();
          this.error = errorMessage;
          this.isLoading = false;
        },
      });
  }

  hasScans(): boolean {
    return this.days.some((day) => day.scanCount > 0);
  }

  formatDate(date: string): string {
    return dayjs(date).format('DD.MM.YYYY');
  }

  formatScanTime(value: string | null): string {
    if (!value) {
      return '–';
    }
    return dayjs(value).format('HH:mm:ss');
  }

  getDateRangeDisplay(): string {
    if (!this.dateFrom || !this.dateTo) {
      return '';
    }
    const fromStr = this.dateFrom.format('DD.MM.YYYY');
    const toStr = this.dateTo.format('DD.MM.YYYY');
    return `${fromStr} - ${toStr}`;
  }

  isCustomRange(): boolean {
    return this.configuration.range === JobDashboardRange.CUSTOM;
  }

  getRangeKey(): string {
    // Convert enum value to key for translation lookup
    const entries = Object.entries(JobDashboardRange);
    const entry = entries.find(
      ([, value]) => value === this.configuration.range,
    );
    return entry ? entry[0] : 'TODAY';
  }

  private resetState(): void {
    this.days = [];
    this.warnings = [];
    this.error = null;
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    // Handle specific error codes from the analytics endpoint
    if (err.status === 408) {
      return this.translate.instant('dashboard.dailyScanTimes.errors.timeout');
    }
    if (err.status === 400) {
      // Try to get the message from the response, fall back to translation
      const message = err.error?.message || err.error?.error;
      if (message) {
        return message;
      }
      return this.translate.instant(
        'dashboard.dailyScanTimes.errors.badRequest',
      );
    }
    // Generic error message
    return this.translate.instant('dashboard.dailyScanTimes.errors.generic');
  }
}
